/**
 * extractFromAliases — pulls outer-scope alias names out of a SELECT outline,
 * for use by `SubquerySection` to detect correlated references in nested
 * subqueries.
 *
 * Walks the comma-separated FROM segments + their JOIN strips and collects:
 *   - the FROM-item's trailing AS-alias (or bare-alias) — `FROM customers c`
 *   - each JOIN strip's trailing AS-alias on the RHS
 *   - if no alias is present, the bare table name
 *
 * Pure / token-aware. Returns a unique-but-order-preserving list of aliases.
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { SelectOutline } from './extractSelectOutline';
import { extractFromSegmentJoins } from './extractFromJoins';
import { splitTopLevelCommas } from './splitTopLevel';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

const RESERVED_AFTER: ReadonlySet<string> = new Set([
  'WHERE', 'GROUP', 'HAVING', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'ON', 'USING', 'AS', 'JOIN', 'INNER',
  'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER', 'NATURAL', 'LATERAL', 'APPLY',
]);

export function extractFromAliases(
  bufferText: string,
  outline: SelectOutline,
  options: ParseOptions,
): string[] {
  if (!outline.from) return [];
  const aliases: string[] = [];
  const segments = splitTopLevelCommas(bufferText, outline.from.start, outline.from.end, options);
  for (const seg of segments) {
    const split = extractFromSegmentJoins(bufferText, seg.start, seg.end, options);
    pushAliasFromSpan(aliases, bufferText, split.leading.start, split.leading.end);
    for (const join of split.joins) {
      pushAliasFromSpan(aliases, bufferText, join.rhs.start, join.rhs.end);
    }
  }
  return aliases;
}

function pushAliasFromSpan(out: string[], source: string, start: number, end: number): void {
  const alias = readTrailingAlias(source, start, end);
  if (alias && !out.includes(alias)) out.push(alias);
}

function readTrailingAlias(source: string, start: number, end: number): string | null {
  let i = end;
  while (i > start && /\s/.test(source[i - 1])) i--;
  if (i <= start) return null;

  const lastEnd = i;
  let lastStart = i;
  while (lastStart > start && IDENT_CONT.test(source[lastStart - 1])) lastStart--;
  if (lastStart >= lastEnd) {
    if (source[lastEnd - 1] === '"' || source[lastEnd - 1] === '`' || source[lastEnd - 1] === ']') {
      return readQuotedTail(source, start, lastEnd);
    }
    return null;
  }
  if (lastStart > start && !IDENT_START.test(source[lastStart])) return null;
  const lastWord = source.slice(lastStart, lastEnd);
  if (RESERVED_AFTER.has(lastWord.toUpperCase())) return null;

  let j = lastStart;
  while (j > start && /\s/.test(source[j - 1])) j--;
  if (j > start + 1 && source.slice(j - 2, j).toUpperCase() === 'AS' &&
      (j === start + 2 || /\s/.test(source[j - 3]))) {
    return lastWord;
  }
  // Bare alias: only if the token before isn't one of the reserved noise (a
  // join keyword or punctuation we skipped). Falls back to "is the last word
  // a plausible alias" — the caller can de-dupe.
  if (lastStart > start) {
    // Scan one more word back to make sure it isn't itself a reserved keyword
    // (e.g. for `FROM t` the previous content is just `FROM` outside our span).
    let prevEnd = lastStart;
    while (prevEnd > start && /\s/.test(source[prevEnd - 1])) prevEnd--;
    let prevStart = prevEnd;
    while (prevStart > start && IDENT_CONT.test(source[prevStart - 1])) prevStart--;
    if (prevEnd > prevStart) {
      const prevWord = source.slice(prevStart, prevEnd).toUpperCase();
      if (RESERVED_AFTER.has(prevWord)) return null;
    }
    return lastWord;
  }
  // Single-word span: that word IS the table name. Use it as the alias.
  return lastWord;
}

function readQuotedTail(source: string, start: number, end: number): string | null {
  const close = source[end - 1];
  const open = close === '"' ? '"' : close === '`' ? '`' : close === ']' ? '[' : null;
  if (!open) return null;
  let j = end - 2;
  while (j > start && source[j] !== open) j--;
  if (j < start || source[j] !== open) return null;
  return source.slice(j + 1, end - 1);
}
